/**
 * AnimatedButton Component
 * Created: 08-10-2025 11:27:08
 * Purpose: Modern 2025 button with rounded-2xl and hover elevation animations
 * 
 * Features:
 * - Rounded corners (16px = rounded-2xl in Tailwind)
 * - Hover elevation with smooth shadow transition
 * - Scale animation on hover (1.02x)
 * - Click scale down (0.98x)
 * - Smooth transitions (200ms ease)
 * - Fully accessible with proper ARIA attributes
 * 
 * @example
 * <AnimatedButton variant="primary" leftSection={<IconPlus />}>
 *   Create Assignment
 * </AnimatedButton>
 */

import React from 'react';
import { Button } from '@mantine/core';

type AnimatedButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

interface AnimatedButtonProps {
  /**
   * Visual variant of the button
   * @default 'primary'
   */
  variant?: AnimatedButtonVariant;
  
  /**
   * Disable hover animations
   * @default false
   */
  disableAnimation?: boolean;
  
  /**
   * Button size
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /**
   * Left section content
   */
  leftSection?: React.ReactNode;
  
  /**
   * Right section content
   */
  rightSection?: React.ReactNode;
  
  /**
   * Click handler
   */
  onClick?: () => void;
  
  /**
   * Button is disabled
   */
  disabled?: boolean;
  
  /**
   * Button is loading
   */
  loading?: boolean;
  
  /**
   * Button children
   */
  children: React.ReactNode;
  
  /**
   * Custom className
   */
  className?: string;
  
  /**
   * Custom style
   */
  style?: React.CSSProperties;
}

export const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  variant = 'primary',
  disableAnimation = false,
  children,
  style,
  size,
  leftSection,
  rightSection,
  onClick,
  disabled,
  loading,
  className,
}) => {
  // Map variants to Mantine button props
  const variantMap = {
    primary: { variant: 'filled', color: 'blue' },
    secondary: { variant: 'light', color: 'blue' },
    danger: { variant: 'filled', color: 'red' },
    ghost: { variant: 'subtle', color: 'blue' },
  } as const;

  const { variant: mantineVariant, color } = variantMap[variant];

  const buttonProps: any = {
    variant: mantineVariant,
    color,
    ...(size && { size }),
    ...(leftSection && { leftSection }),
    ...(rightSection && { rightSection }),
    ...(onClick && { onClick }),
    ...(disabled !== undefined && { disabled }),
    ...(loading !== undefined && { loading }),
    ...(className && { className }),
    style: {
      borderRadius: '16px',
      transition: disableAnimation ? 'none' : 'all 200ms ease',
      fontWeight: 500,
      ...style,
    },
    onMouseEnter: (e: any) => {
      if (!disableAnimation && !disabled) {
        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
        e.currentTarget.style.boxShadow = variant === 'primary'
          ? '0 8px 16px rgba(34, 139, 230, 0.3)'
          : '0 8px 16px rgba(0, 0, 0, 0.1)';
      }
    },
    onMouseLeave: (e: any) => {
      if (!disableAnimation) {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'none';
      }
    },
    onMouseDown: (e: any) => {
      if (!disableAnimation && !disabled) {
        e.currentTarget.style.transform = 'scale(0.98)';
      }
    },
    onMouseUp: (e: any) => {
      if (!disableAnimation && !disabled) {
        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
      }
    },
  };

  return <Button {...buttonProps}>{children}</Button>;
};

export default AnimatedButton;

